import { useState } from 'react'
import { Chip, type ChipTone, KpiTile, Panel, Pipe, Tabs } from '@/components/redesign/bits'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { formatDateTime, formatRelative, numberFmt } from '@/lib/format'
import {
  type ConnectionDays,
  type ConnectionProblem,
  type ConnectionTarget,
  useConnectionErrors,
} from './api'

const PERIODS: { key: `${ConnectionDays}`; label: string }[] = [
  { key: '1', label: 'Today' },
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
]

const WHERE: Record<ConnectionTarget, { label: string; sub: string; dot: string }> = {
  service: { label: 'Our services', sub: 'Google, DataForSEO, Lemon Squeezy…', dot: 'bad' },
  internal: { label: 'Arva SEO itself', sub: 'its own addresses and helpers', dot: 'bad' },
  api: { label: 'Browsers to Arva SEO', sub: 'the app and control panel', dot: 'warn' },
  website: { label: 'Customers’ websites', sub: 'their sites and WordPress', dot: '' },
}

const KIND: Record<string, { label: string; tone: ChipTone }> = {
  dns: { label: 'Address lookup', tone: 'bad' },
  refused: { label: 'Refused', tone: 'bad' },
  tls: { label: 'Secure connection', tone: 'bad' },
  timeout: { label: 'Timed out', tone: 'warn' },
  reset: { label: 'Dropped', tone: 'warn' },
  gateway: { label: 'Gateway error', tone: 'warn' },
  network: { label: 'No answer', tone: 'warn' },
  other: { label: 'Other', tone: '' },
}

/**
 * Connections that failed and that Sentry doesn't see, because the code handles them (owner,
 * 2026-10-23): the server reaching the services Arva SEO uses and customers' websites, and
 * browsers reaching the API. Owners and admins are told when one keeps happening.
 */
export function ConnectionsPage() {
  const [days, setDays] = useState<ConnectionDays>(7)
  const [where, setWhere] = useState<ConnectionTarget | null>(null)
  const errors = useConnectionErrors(days)

  return (
    <>
      <PageTitle
        title="Connection errors"
        description="Connections that failed without reaching Sentry, because Arva SEO handled them quietly: the server reaching the services it uses or a customer’s website, and browsers reaching the API. Owners and admins get a notification and an email when one keeps happening, and a summary each morning."
      />
      {errors.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : errors.isError ? (
        <div className="card">
          <ErrorState error={errors.error} onRetry={() => void errors.refetch()} />
        </div>
      ) : (
        <div className="sec" style={{ gap: 20 }}>
          <Tabs
            label="Period"
            tabs={PERIODS}
            value={`${days}`}
            onChange={(key) => setDays(Number(key) as ConnectionDays)}
          />
          <div className="kpis">
            <KpiTile
              label="Today"
              value={numberFmt.format(errors.data.meta.today)}
              compare="failed connections"
            />
            {days === 1 ? (
              <KpiTile
                label="Problems"
                value={numberFmt.format(errors.data.data.length)}
                compare="different hosts and kinds"
              />
            ) : (
              <KpiTile
                label={`Last ${days} days`}
                value={numberFmt.format(errors.data.meta.total)}
                compare={`in ${numberFmt.format(errors.data.data.length)} ${errors.data.data.length === 1 ? 'problem' : 'problems'}`}
              />
            )}
            <KpiTile
              label="Our services"
              value={numberFmt.format(
                count(errors.data.data, 'service') + count(errors.data.data, 'internal'),
              )}
              compare="what to fix first"
            />
            <KpiTile
              label="Browsers to Arva SEO"
              value={numberFmt.format(count(errors.data.data, 'api'))}
              compare="people who couldn’t reach the API"
            />
          </div>
          <Pipe
            items={(Object.keys(WHERE) as ConnectionTarget[]).map((key) => ({
              key,
              label: WHERE[key].label,
              count: numberFmt.format(count(errors.data.data, key)),
              dot: WHERE[key].dot,
              sub: WHERE[key].sub,
            }))}
            value={where}
            onChange={setWhere}
          />
          <ProblemTable
            problems={errors.data.data.filter((p) => where === null || p.target === where)}
            days={days}
            keepDays={errors.data.meta.keep_days}
          />
          <Panel
            title="When you’re told"
            sub="One notification for everything due, never more than every 10 minutes"
          >
            <ul className="grid gap-2 text-[13px] text-ink-muted">
              <li>
                Our services and Arva SEO itself: after one address lookup, refused or secure-connection
                failure, or three timeouts, dropped connections or gateway errors.
              </li>
              <li>
                Browsers to Arva SEO: after five times nobody could reach the API, or three gateway errors.
              </li>
              <li>
                Customers’ websites: only when three or more fail within 30 minutes, which points to our side.
                One website being down is in the morning summary.
              </li>
              <li>
                The same problem is mentioned again after six hours at the earliest, if it’s still happening.
              </li>
              <li>
                Outside links an audit checks never count, and only the host and the kind of problem are kept,
                for {errors.data.meta.keep_days} days.
              </li>
            </ul>
          </Panel>
        </div>
      )}
    </>
  )
}

function count(problems: ConnectionProblem[], target: ConnectionTarget) {
  return problems.filter((p) => p.target === target).reduce((sum, p) => sum + p.count, 0)
}

function ProblemTable({
  problems,
  days,
  keepDays,
}: {
  problems: ConnectionProblem[]
  days: ConnectionDays
  keepDays: number
}) {
  if (problems.length === 0) {
    return (
      <div className="card pad">
        <p className="empty-line">
          {days === 1 ? 'No failed connections today.' : `No failed connections in the last ${days} days.`}{' '}
          Rows are kept for {keepDays} days.
        </p>
      </div>
    )
  }

  return (
    <div className="tw">
      <table className="t" style={{ minWidth: 820 }}>
        <caption className="sr-only">Failed connections, the latest first</caption>
        <thead>
          <tr>
            <th>Problem</th>
            <th>Kind</th>
            <th>Where</th>
            <th className="r">Times</th>
            <th className="r">Today</th>
            <th>Last seen</th>
            <th>Told you</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((p) => (
            <tr key={p.key}>
              <td style={{ maxWidth: 380 }}>
                <b style={{ wordBreak: 'break-all' }}>{p.summary}</b>
                {p.sample && (
                  <div className="m" style={{ fontSize: 12, wordBreak: 'break-word' }}>
                    {p.sample}
                  </div>
                )}
              </td>
              <td>
                <Chip tone={KIND[p.kind]?.tone ?? ''}>{KIND[p.kind]?.label ?? p.kind}</Chip>
              </td>
              <td className="m">{WHERE[p.target].label}</td>
              <td className="r num">{numberFmt.format(p.count)}</td>
              <td className="r num">{numberFmt.format(p.today)}</td>
              <td className="m" style={{ whiteSpace: 'nowrap' }} title={formatDateTime(p.last_seen_at)}>
                {formatRelative(p.last_seen_at)}
              </td>
              <td className="m" style={{ whiteSpace: 'nowrap' }}>
                {p.alerted_at ? formatRelative(p.alerted_at) : 'Not yet'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
